// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * THE DISMISSAL CONTRACT.
 *
 * Every overlay ships all four exits, and they are not alternatives:
 *
 *   1. an × control inside the panel, with a 44x44 hit area
 *   2. a tap or click anywhere outside the panel
 *   3. Escape, from anywhere on the page
 *   4. the same outside tap as a real touch sequence
 *
 * Exit 2 is wired on `pointerdown`, which covers mouse, touch and pen in one
 * handler and fires where a synthetic `click` would not. Nothing here is gated
 * on re-pressing the control that opened the overlay: on a phone the overlay
 * covers it.
 *
 * The `[hidden]` guard in main.css is what makes `hidden` authoritative. An
 * author rule like `.modal { display: flex }` outranks the browser's
 * `[hidden] { display: none }` — author origin beats user-agent regardless of
 * specificity — so without the guard a "hidden" full-screen overlay stays
 * visible and silently swallows every tap on the page beneath it.
 */

interface OpenModal {
  readonly root: HTMLElement;
  readonly opener: HTMLElement | null;
}

const stack: OpenModal[] = [];

export function anyOpen(): boolean {
  return stack.length > 0;
}

export function closeTop(): void {
  const top = stack.pop();
  if (!top) return;
  top.root.hidden = true;
  top.opener?.focus();
}

function closeAll(): void {
  while (stack.length > 0) closeTop();
}

/**
 * Build a modal from a <template> in index.html.
 *
 * The content lives in the document rather than in TypeScript so the prose is
 * readable, reviewable and diffable as prose, and so the honesty gate can scan it
 * as HTML rather than as string literals.
 */
export function createModal(id: string, title: string, templateId: string): HTMLElement {
  const root = document.createElement('div');
  root.className = 'modal-host';
  root.id = id;
  root.hidden = true;

  // A real hit area: fixed, inset 0, below the panel and above the page, and
  // never pointer-events: none — an unreachable backdrop is exit 2 silently
  // missing.
  const backdrop = document.createElement('div');
  backdrop.className = 'modal-backdrop';

  const panel = document.createElement('div');
  panel.className = 'modal-panel';
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-labelledby', `${id}-title`);
  panel.tabIndex = -1;

  const header = document.createElement('header');
  header.className = 'modal-header';
  const heading = document.createElement('h2');
  heading.id = `${id}-title`;
  heading.textContent = title;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'icon-btn';
  close.setAttribute('aria-label', `Close ${title}`);
  close.textContent = '×';

  header.append(heading, close);

  const content = document.createElement('div');
  content.className = 'modal-content';
  const template = document.getElementById(templateId) as HTMLTemplateElement | null;
  if (template) content.appendChild(template.content.cloneNode(true));

  panel.append(header, content);
  root.append(backdrop, panel);

  close.addEventListener('click', () => closeTop());
  // pointerdown on the backdrop: fires for mouse, touch and pen, and fires in
  // cases where a synthetic click does not.
  backdrop.addEventListener('pointerdown', (ev) => {
    ev.preventDefault();
    closeTop();
  });

  document.body.appendChild(root);
  return root;
}

export function openModal(root: HTMLElement, opener: HTMLElement | null): void {
  root.hidden = false;
  stack.push({ root, opener });
  (root.querySelector('.modal-panel') as HTMLElement | null)?.focus();
}

/** Wire Escape once, for every overlay. */
export function mountModalKeys(onEscape?: () => boolean): void {
  document.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Escape') return;
    // The modal stack first, then anything else the page wants to close.
    if (stack.length > 0) {
      closeTop();
      ev.stopPropagation();
      return;
    }
    onEscape?.();
  });

  // A page-level pointerdown so a modal opened without a reachable backdrop
  // still closes on an outside press. Belt and braces, deliberately: the whole
  // point of the contract is that no single mechanism is the only way out.
  document.addEventListener('pointerdown', (ev) => {
    if (stack.length === 0) return;
    const top = stack[stack.length - 1];
    const panel = top.root.querySelector('.modal-panel');
    if (panel && !panel.contains(ev.target as Node)) closeTop();
  });

  window.addEventListener('pagehide', closeAll);
}
